import type {
  CellContext,
  ColumnDef,
  ColumnDefTemplate,
} from '@tanstack/react-table';
import { useMemo, type JSX } from 'react';

import type {
  SimpleTableV2ColumnDef,
  SimpleTableV2DisplayColumnDef,
  SimpleTableV2GroupColumnDef,
  SimpleTableV2RowData,
} from '../public.api.js';

/**
 * Custom cell renderer function.
 * @public
 */
export type SimpleTableV2CustomCell<
  TData extends SimpleTableV2RowData,
  TValue = unknown,
> = (props: {
  /** Raw value of the cell that is rendered. */
  value: TValue;
  /** Index of the row the rendered cell is in. */
  rowIndex: number;
  /** Data of the entire row the rendered cell is in. */
  rowData: TData;
  /** Id of the row the rendered cell is in. */
  rowId: string;
}) => JSX.Element;

/**
 * @internal
 * TypeGuard helper to verify that a given external columnDef is a GroupColumnDef
 */
export function isSimpleTableV2GroupColumnDef<
  TData extends SimpleTableV2RowData,
  TValue = unknown,
>(columnDef: unknown): columnDef is SimpleTableV2GroupColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'columns' in columnDef &&
    Array.isArray((columnDef as { columns: Array<unknown> }).columns)
  );
}

/**
 * @internal
 * TypeGuard helper to verify that a given external columnDef is a DisplayColumnDef
 */
function isSimpleTableV2DisplayColumnDef<
  TData extends SimpleTableV2RowData,
  TValue = unknown,
>(
  columnDef: unknown,
): columnDef is SimpleTableV2DisplayColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'accessor' in columnDef
  );
}

/**
 * @internal
 * Helper function to transform the public accessor key to the correct
 * member config for the tanstack/react-table ColumnDef.
 */
function splitAccessor<TData extends SimpleTableV2RowData, TValue = unknown>(
  column: SimpleTableV2DisplayColumnDef<TData, TValue>,
): {
  accessorKey?: string;
  accessorFn?: (row: TData) => TValue;
} {
  if (typeof column.accessor === 'string') {
    return {
      accessorKey: column.accessor,
    };
  }

  if (typeof column.accessor === 'function') {
    return {
      accessorFn: column.accessor,
    };
  }
  return {};
}

/**
 * Maps the given header renderer to the internal header renderer and prevents
 * leaking.
 * @internal
 */
function createHeaderRenderer<TData extends SimpleTableV2RowData>(
  header: SimpleTableV2ColumnDef<TData>['header'],
) {
  // We need to map the function from the outside here in order to not leak
  // tanstack table internals.
  if (typeof header === 'function') {
    return () => header();
  }
  return header;
}

/**
 * @internal
 * Helper function to convert from the public `SimpleTableV2DisplayColumnDef` to the internally used
 * ColumnDef.
 */
function createDisplayColumnDef<
  TData extends SimpleTableV2RowData,
  TValue = unknown,
>(
  columnDef: SimpleTableV2DisplayColumnDef<TData, TValue>,
): ColumnDef<TData, TValue> {
  const composedDef: ColumnDef<TData, TValue> = {
    id: columnDef.id,
    header: createHeaderRenderer(columnDef.header),
    meta: {
      width: columnDef.width,
      minWidth: columnDef.minWidth,
      maxWidth: columnDef.maxWidth,
      alignment: columnDef.alignment ?? 'left',
    },
    ...splitAccessor(columnDef),
  };

  if (columnDef.cell) {
    const customCell = columnDef.cell;

    if (typeof customCell === 'string') {
      composedDef.cell = customCell;
      return composedDef;
    }

    if (typeof customCell === 'function') {
      return {
        ...composedDef,
        cell: createCellRenderer(customCell),
      };
    }

    return composedDef;
  }

  return composedDef;
}

/**
 * Prepares all exposed functions for the custom cell renderer.
 */
function createCellRenderer<TData extends SimpleTableV2RowData, TValue>(
  consumerCustomCell: SimpleTableV2CustomCell<TData, TValue>,
): ColumnDefTemplate<CellContext<TData, TValue>> {
  return ({ getValue, row, column }) => {
    return consumerCustomCell({
      value: getValue(),
      rowIndex: row.index,
      rowData: row.original,
      rowId: row.id,
    });
  };
}

/**
 * Helper hook that will calculate which columns are enabled within the SimpleTable
 * @internal
 */
export function useColumns<
  TData extends SimpleTableV2RowData,
  TValue = unknown,
>(propColumns: SimpleTableV2ColumnDef<TData, TValue>[]) {
  return useMemo(() => {
    const createdColumns: ColumnDef<TData, TValue>[] = [];

    // Iterate through the columns and create the definitions required
    for (const firstLevelColumn of propColumns) {
      if (isSimpleTableV2GroupColumnDef(firstLevelColumn)) {
        const groupColumn: ColumnDef<TData, TValue> = {
          id: firstLevelColumn.id,
          header: createHeaderRenderer(firstLevelColumn.header),
          columns: firstLevelColumn.columns?.map((childColumn) =>
            createDisplayColumnDef<TData>(childColumn),
          ),
          meta: {
            width: firstLevelColumn.width,
            alignment: firstLevelColumn.alignment ?? 'left',
          },
        };
        createdColumns.push(groupColumn);
      } else if (isSimpleTableV2DisplayColumnDef(firstLevelColumn)) {
        createdColumns.push(createDisplayColumnDef<TData>(firstLevelColumn));
      }
    }

    return createdColumns;
  }, [propColumns]);
}
